<template lang="html">
  <div
    class="cart flex flex-col gap-6 overflow-hidden [@media(max-height:700px)]:overflow-auto"
  >
    <div class="cart__header flex flex-wrap items-center gap-2 font-bold">
      <span>Корзина</span>
      <span>[{{ totalLength }}]</span>

      <UButton
        v-if="availableItems.length"
        variant="ghost"
        size="sm"
        @click="toggleSelectAll"
      >
        {{ allSelected ? 'Снять выбор' : 'Выбрать всё' }}
      </UButton>

      <UButton
        icon="i-lucide-x"
        class="ml-auto"
        size="lg"
        variant="ghost"
        @click="emit('close')"
      />
    </div>

    <div
      :class="[
        'cart__body relative flex flex-1 flex-col gap-4 [@media(min-height:699px)]:overflow-auto',
        { 'justify-center-safe': !cartItems.length },
      ]"
    >
      <UAlert
        v-if="isAnonymous"
        color="info"
        icon="i-lucide-triangle-alert"
        variant="soft"
        class="shrink-0"
        title="Вы не авторизованы"
        description="Как гость, вы не сможете отслеживать заказ в личном кабинете."
        :actions="[
          {
            label: 'Войти или зарегистрироваться',
            onClick: () => openAuth(),
          },
        ]"
      />

      <NonePanel v-if="!cartItems.length" key="none" class="border-0!" />

      <AppLoader
        v-if="isLoading"
        key="loader"
        class="absolute top-[50%] left-[50%] h-20 w-20 -translate-1/2 text-tertiary-500"
      />

      <CartList
        selectable
        :value="availableCartItems"
        :attributes-meta="store.cart?.attributesMeta"
        :selected="selectedProductIds"
        :line-unit-prices="lineUnitPriceByProductId"
        @remove="handleRemove"
        @update:selected="handleSelectedChange"
      />

      <div v-if="unavailableCartItems.length">
        <div class="mb-1 text-sm font-bold text-muted">
          Нет в наличии ({{ unavailableCartItems.length }})
        </div>
        <CartList
          :value="unavailableCartItems"
          :attributes-meta="store.cart?.attributesMeta"
          show-notify
          @remove="handleRemove"
          @toggle-notify="handleToggleNotify"
        />
      </div>

      <div class="cart__promo flex flex-col gap-4">
        <UAlert
          v-if="isAnonymous"
          color="info"
          icon="i-lucide-triangle-alert"
          variant="soft"
          title="Акции применяются после входа"
          description="Гостю акции, промокод и баллы не применяются — ни в корзине, ни при оформлении. Суммы ниже посчитаны без скидок. Войдите или зарегистрируйтесь, чтобы применить акции."
          :actions="[
            {
              label: 'Войти или зарегистрироваться',
              onClick: () => openAuth(),
            },
          ]"
        />

        <PromoCodeInput
          v-if="!isAnonymous"
          v-model="inputCode"
          :active-promo-code="activePromoCode"
          :error="promoCodeError"
          :loading="promoLoading"
          :has-saved-promo-without-selection="hasSavedPromoWithoutSelection"
          @apply="handleApplyPromo"
          @clear="handleClearPromo"
        />

        <PointsRedemptionInput
          v-if="!isAnonymous"
          v-model="pointsToSpend"
          :wallet-balance="pricing?.walletBalance ?? 0"
          :max-points-redeemable="pricing?.maxPointsRedeemable ?? 0"
        />

        <PromotionStatusList
          :applied="pricing?.appliedPromotions ?? []"
          :rejected="pricing?.rejectedPromotions ?? []"
          :active-promo-code="activePromoCode"
        />
      </div>
    </div>

    <div
      v-if="cartItems.length || activePromoCode"
      key="footer"
      class="cart__footer flex flex-col gap-4 border-t-2 border-default pt-4"
    >
      <div v-if="availableItems.length" class="flex flex-col gap-2">
        <div class="flex justify-between">
          <span class="text-muted">Выбрано {{ selectedProductIds.length }} из
            {{ availableItems.length }}</span>
        </div>

        <div v-if="showDiscount" class="flex justify-between text-sm">
          <span class="text-muted">Сумма товаров:</span>
          <span>{{ $f.currency(pricing?.cartBase ?? fallbackCost) }}</span>
        </div>

        <div
          v-if="showDiscount"
          class="flex justify-between text-sm text-success"
        >
          <span>Скидка:</span>
          <span>−{{ $f.currency(pricing!.discountTotal) }}</span>
        </div>

        <div
          v-if="showPointsApplied"
          class="flex justify-between text-sm text-success"
        >
          <span>Баллы:</span>
          <span>−{{ $f.currency(pricing!.pointsApplied) }}</span>
        </div>

        <div
          v-if="showCashbackAccrual"
          class="flex justify-between text-sm text-info"
        >
          <span>Начислим кэшбэк:</span>
          <span>+{{ $f.currency(pricing!.cashbackAccrualTotal) }}</span>
        </div>

        <div class="flex justify-between">
          <span class="font-bold">Итого:</span>
          <span class="font-bold">{{ $f.currency(selectedTotal) }}</span>
        </div>
      </div>

      <div class="cart__actions">
        <UButton
          block
          :disabled="!canCheckout"
          class="mt-2"
          @click="handleCheckout"
        >
          Оформить заказ ({{ selectedProductIds.length }})
        </UButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCartModule } from '../../composables/useCartModule'
import { useCartSelection } from '../../composables/useCartSelection'
import CartList from '../Info/CartList.vue'

import PointsRedemptionInput from '#Promotion/components/PointsRedemptionInput.vue'
import PromoCodeInput from '#Promotion/components/PromoCodeInput.vue'
import PromotionStatusList from '#Promotion/components/PromotionStatusList.vue'
import { useCartPricing } from '#Promotion/composables/useCartPricing'
import { usePromoCode } from '#Promotion/composables/usePromoCode'
import AppLoader from '~/app/components/Loader/AppLoader.vue'

const emit = defineEmits<{
  close: []
}>()

const { store } = useCartModule()
const { removeFromCart, toggleNotify } = useCart()
const { send, isLoading } = useRequest()
const {
  routing: orderRouting,
  support: orderSupport,
  service: orderService,
} = useOrder()
const { store: authStore } = useAuth()
const { openAuth } = useAuthInteractive()
const { $f } = useFormatter()
const {
  cartItems,
  availableCartItems,
  unavailableCartItems,
  availableItems,
  selectedProductIds,
  selectedItems,
  allSelected,
  canCheckout,
  toggleSelectAll,
  handleSelectedChange,
} = useCartSelection()

const {
  pricing,
  refreshPricing,
  lineUnitPriceByProductId,
  activePromoCode,
  pointsToSpend,
} = useCartPricing(selectedItems)

const promoLoading = ref(false)

const hasSelectedItems = computed(() => selectedItems.value.length > 0)

const {
  inputCode,
  promoCodeError,
  applyPromoCode,
  clearPromoCode,
  hasSavedPromoWithoutSelection,
} = usePromoCode(refreshPricing, hasSelectedItems)

const totalLength = computed(() => store.totalLength)
const isAnonymous = computed(() => Boolean(authStore.session?.isAnon))

const fallbackCost = computed(() =>
  orderService.calculateItemsCost(selectedItems.value),
)

const selectedTotal = computed(() => {
  if (pricing.value) {
    return pricing.value.payable ?? pricing.value.payableBeforePoints
  }

  return fallbackCost.value
})

const showPointsApplied = computed(() =>
  Boolean(pricing.value && (pricing.value.pointsApplied ?? 0) > 0),
)

const showDiscount = computed(() =>
  Boolean(pricing.value && (pricing.value.discountTotal ?? 0) > 0),
)

const showCashbackAccrual = computed(() =>
  Boolean(pricing.value && (pricing.value.cashbackAccrualTotal ?? 0) > 0),
)

async function handleApplyPromo() {
  promoLoading.value = true

  try {
    await applyPromoCode()
  } finally {
    promoLoading.value = false
  }
}

async function handleClearPromo() {
  promoLoading.value = true

  try {
    await clearPromoCode()
  } finally {
    promoLoading.value = false
  }
}

function handleCheckout() {
  orderSupport.saveItemsForOrder(selectedItems.value)
  navigateTo(orderRouting.create.route)
  emit('close')
}

function handleRemove(productId: number) {
  send(() => removeFromCart(productId, 1), {
    key: 'remove',
  })
}

function handleToggleNotify(productId: number) {
  send(() => toggleNotify(productId), {
    key: 'toggleNotify',
    successMessage: 'Статус отслеживания обновлён',
  })
}
</script>
